import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { contrastChart, Spinner, Button, Icon } from '@iq/react-components';
import { ResponsiveContainer, PieChart, Pie, Legend, LabelList, Label } from 'recharts';

import classnames from 'classnames';
import Loader from '../../../Loader';
import { getVariableById } from '../../../../bundles/sources';
import { useSubscription } from '../../../../bundles/visualizations';
import { format } from '../../../../units';
import { useClientSize } from '../../../../utils';
import { THRESHOLD_TYPES } from '../../../../constants';
import { lightenDarkenColor } from '../../../../colors';
import { DropdownMenu } from '../../../DropdownMenu';
import LabelWithTooltip from '../../../LabelWithTooltip';

const alignValue = (value, min, max) => Math.max(min, Math.min(value, max));
const truncateDisplayText = (text, maxChars = 10) => {
  if (!text) return '';
  return text.length > maxChars ? `${text.slice(0, maxChars - 1)}…` : text;
};

const CustomLegend = ({ thresholdsData }) => {
  const items = thresholdsData[THRESHOLD_TYPES[thresholdsData.thresholdType]?.key || ''] || [];
  const soretdThresholds = items.toSorted((a, b) => {
    if (thresholdsData.thresholdType === THRESHOLD_TYPES.area.value) {
      return a.min - b.min;
    }
    return a.value - b.value;
  });
  return (
    <ul className="gauge-legend">
      {soretdThresholds.map((threshold, index) => {
        const displayText =
          thresholdsData.thresholdType === THRESHOLD_TYPES.area.value
            ? threshold.description || `${threshold.max}`
            : threshold.description || `${threshold.value}`;

        return (
          <li
            key={`${threshold.fill}-${index}`}
            className="gauge-legend-item"
          >
            <svg
              width="10"
              height="10"
            >
              <rect
                width="10"
                height="10"
                fill={threshold.fill}
              />
            </svg>
            <span title={displayText}>{truncateDisplayText(displayText)}</span>
          </li>
        );
      })}
    </ul>
  );
};

const GaugeVisualization = ({
  visualization,
  panelId,
  onFullscreen,
  isFullscreen,
  isPreview,
  getMoreOptions,
  dropdownPlacement,
  disconnected,
}) => {
  const {
    latestValues: values,
    initialLoaded,
    loading,
  } = useSubscription(visualization.id, panelId);
  const [firstVariable] = values || [];
  const { variable: variableId, value: firstVariableValue = [] } = firstVariable || {};
  const sourceVariable = useSelector((state) => getVariableById(state, variableId));
  const [{ height }, clientReference] = useClientSize();
  const { configuration = {} } = visualization;
  const { thresholds = {}, min = 0, max = 100 } = configuration;
  const currentThresholdKey = THRESHOLD_TYPES[thresholds.thresholdType]?.key || '';
  const isAreaThresholds = currentThresholdKey === THRESHOLD_TYPES.area.key;

  const range = max - min;

  const getThresholdValue = (item) => {
    if (isAreaThresholds) {
      return item.min;
    }
    return item.value;
  };

  const sortedThresholds = useMemo(() => {
    if (thresholds[currentThresholdKey]) {
      return thresholds[currentThresholdKey].toSorted(
        (a, b) => getThresholdValue(a) - getThresholdValue(b)
      );
    }
    return [];
  }, [thresholds, currentThresholdKey]);

  const [value, quality] = useMemo(() => {
    if (initialLoaded) {
      const [, lastValue, lastValueQuality] = firstVariableValue;
      if (lastValue || lastValue === 0) {
        const currentValue = Number.isNaN(lastValue) ? null : alignValue(lastValue, min, max);
        return [currentValue, lastValueQuality];
      }
    }
    return [null, 0];
  }, [firstVariableValue, initialLoaded, min, max]);

  const thresholdsPercentageWidth = 0.015;
  const thresholdWidth = Number.parseInt(range * thresholdsPercentageWidth, 8);

  /**
   * Create all threshold slices to fill the gauge (normalized to range).
   */
  const { slices } = sortedThresholds.reduce(
    (accumulator, current, index) => {
      if (accumulator.runningTotal >= range) return accumulator;
      let thWidth = thresholdWidth;

      if (isAreaThresholds) {
        const diff = current.max - current.min;
        const percentageOfRange = diff / range;
        const trueWidth = range * percentageOfRange;
        thWidth = Math.min(trueWidth, range);
      }

      const thresholdFill = lightenDarkenColor(current.fill, -10);

      // Normalize threshold value relative to min
      const normalizedThreshold = getThresholdValue(current) - min;
      let buffertWidth = normalizedThreshold - accumulator.runningTotal;

      if (isAreaThresholds) {
        const normalizedCurrentMin = current.min - min;
        const diff = normalizedCurrentMin - accumulator.runningTotal;
        const percentageOfRange = diff / range;
        buffertWidth = range * percentageOfRange;
      }

      // Check if threshold is within the normalized range
      let addSlices = normalizedThreshold - thWidth <= range && normalizedThreshold >= 0;
      if (isAreaThresholds) {
        const normalizedCurrentMin = current.min - min;
        const normalizedCurrentMax = current.max - min;
        addSlices =
          normalizedCurrentMin + thWidth <= range &&
          current.min < current.max &&
          normalizedCurrentMax <= range &&
          normalizedCurrentMin >= 0;
      }

      if (addSlices) {
        // Add buffer and threshold only if the threshold is within range
        accumulator.slices.push(
          { value: buffertWidth, fill: 'transparent' },
          { ...current, fill: thresholdFill, value: thWidth }
        );
        accumulator.runningTotal += buffertWidth + thWidth;
      }

      // Add a last buffer slice if needed, to fill the rest of the gauge
      if (index === sortedThresholds.length - 1 && accumulator.runningTotal < range) {
        buffertWidth = Math.max(
          accumulator.slices.reduce(
            (innerAccumulator, current) => innerAccumulator - current.value,
            range
          ),
          0
        );

        if (isAreaThresholds) {
          buffertWidth = range - accumulator.runningTotal;
        }

        accumulator.slices.push({ value: buffertWidth, fill: 'transparent' });
      }

      return accumulator;
    },
    { runningTotal: 0, slices: [] }
  );

  const [fill] = sortedThresholds.reduce(
    (accumulator, item) => {
      if (isAreaThresholds) {
        if (value >= item.min && value <= item.max && item.min < item.max && item.max <= max) {
          return [item.fill, item.description];
        }
        return accumulator;
      }
      return value >= getThresholdValue(item) ? [item.fill, item.description] : accumulator;
    },
    ['grey', '']
  );

  // Normalize value for data calculation
  const normalizedValue = value === null ? 0 : value - min;

  const data = [
    { value: normalizedValue, fill, fillOpacity: isAreaThresholds ? '100%' : '68%' },
    { value: range - normalizedValue, fill: 'transparent' },
  ];

  const unitLabel =
    configuration.unit !== 'variable-default' && !configuration.unitLabel
      ? configuration.unit
      : configuration.unitLabel;

  const displayUnit =
    configuration.unit === 'variable-default' ? sourceVariable?.unit || '' : configuration.unit;
  const { value: formattedValue, unit } = format(
    displayUnit,
    value,
    configuration.decimals,
    unitLabel
  );

  const renderArrow = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      viewBox: { cx, cy, innerRadius, startAngle },
      value: valueLocal,
    } = props;
    const sin = Math.sin(-RADIAN * startAngle);
    const cos = Math.cos(-RADIAN * startAngle);
    const sx = cx - innerRadius * cos;
    const sy = cy + innerRadius * sin;

    // Fixed rotation calculation to account for min value
    const normalizedValue = valueLocal - min;
    const rotation = -100 + (normalizedValue / range) * 200;

    return (
      <g
        className="gauge-value-marker"
        transform={`rotate(${rotation},${sx},${sy})`}
      >
        <polygon
          points={`${sx},${sy} ${sx + 5},${sy + 10} ${sx - 5},${sy + 10}`}
          stroke="3"
          fill="black"
        />
      </g>
    );
  };

  const vizHeight = height - 28; // less header
  const loaderHeight = vizHeight - 28; // less loader padding

  const classes = classnames('menu', {});

  // invalidate if quality !== 1 (valid) or no data connectivity (disconnected)
  const isDataInvalid = quality > 1 || disconnected;

  /**
   * CustomLabel component for rendering a centered label inside a Pie chart.
   * It handles truncation based on available space and supports optional unit and description.
   */

  const CustomLabel = ({ labelValue, viewBox }) => {
    const { cx, cy, outerRadius } = viewBox;

    // Estimate the maximum width available for the label
    const maxWidth = outerRadius * 2;

    // Approximate character width in pixels (adjust based on font)
    const charWidth = 16;
    const maxChars = Math.floor(maxWidth / charWidth);

    // Get and truncate unit if longer than 5 characters
    const rawUnit = configuration.unitLabel || unit;
    const truncatedUnit = rawUnit && rawUnit.length > 5 ? `${rawUnit.slice(0, 5)}…` : rawUnit;

    // Truncate the value if it exceeds the maximum character count
    const truncatedValue =
      labelValue?.toString().length > maxChars
        ? `${labelValue.toString().slice(0, maxChars - 1)}…`
        : labelValue;

    // Compose the full value with unit (for tooltip)
    const fullValue = `${labelValue ?? '-'} ${rawUnit}`;

    return (
      <text
        x={cx}
        y={cy + 38}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="1.2rem"
        fontWeight="500"
        transform="translate(0, -18)"
        className="gauge-value-label"
      >
        {/* Tooltip for full value on hover */}
        <title>{fullValue}</title>
        {initialLoaded ? truncatedValue : '-'}
        {truncatedUnit && ` ${truncatedUnit}`}
      </text>
    );
  };

  return (
    <div
      className={`gauge-visualization-component ${isDataInvalid ? 'invalid-data' : ''}`}
      ref={clientReference}
    >
      {!isFullscreen && (
        <div className="gauge-header">
          <div className="gauge-title truncateText">
            <span className="title">
              <LabelWithTooltip name={configuration?.title || '\u00A0'} />
            </span>
            {isDataInvalid ? (
              <Button
                tooltip="Invalid signal value"
                design="text"
                tooltipPosition="bottom-end"
              >
                <Icon
                  icon="he-help"
                  size="s"
                  color="#FF7300"
                />
              </Button>
            ) : null}
          </div>
          {!isPreview && (
            <div className={classes}>
              {initialLoaded && loading && (
                <Spinner
                  size="s"
                  className="spinner"
                />
              )}
              <DropdownMenu
                trigger="he-moreoptionsvertical"
                triggerSize="s"
                menuOptions={getMoreOptions({ loading, isPreview, data, onFullscreen })}
                menuXPlacement={dropdownPlacement?.x}
                menuYPlacement={dropdownPlacement?.y}
                styles={{
                  dropdownIndicator: { padding: '0px' },
                  option: { cursor: 'pointer' },
                  menu: { minWidth: 'auto', zIndex: 2001 },
                }}
              />
            </div>
          )}
        </div>
      )}
      {!initialLoaded && loading ? (
        <Loader
          text="Loading ..."
          height={loaderHeight}
        />
      ) : (
        <>
          <div className="gauge-wrapper">
            <ResponsiveContainer
              width="100%"
              height={vizHeight}
            >
              <PieChart>
                {/* This is the arrow marker and a transparent bar */}

                <Pie
                  startAngle={((range - data[1].value) / range) * 200 - 10}
                  endAngle={-10}
                  data={data}
                  dataKey="value"
                  innerRadius="90%"
                  outerRadius="100%"
                  fill="transparent"
                  stroke={0}
                  isAnimationActive={false}
                >
                  <LabelList content={renderArrow} />
                </Pie>

                {/*
                  Renders the default background bar
                */}

                <Pie
                  startAngle={190}
                  endAngle={-10}
                  data={[{ fill: '#D9D9D9', value: range }]}
                  innerRadius="90%"
                  outerRadius="100%"
                  dataKey="value"
                  stroke={0}
                  isAnimationActive={false}
                >
                  <Label content={<CustomLabel labelValue={formattedValue} />} />
                </Pie>

                {/* This is the value bar */}
                {currentThresholdKey === THRESHOLD_TYPES.area.key ? null : (
                  <Pie
                    startAngle={190}
                    endAngle={-10}
                    data={data}
                    dataKey="value"
                    innerRadius="90%"
                    outerRadius="100%"
                    stroke={0}
                    isAnimationActive={false}
                  />
                )}

                {/* This is the thresholds */}

                <Pie
                  startAngle={190}
                  endAngle={-10}
                  data={slices}
                  dataKey="value"
                  innerRadius="90%"
                  outerRadius={isAreaThresholds ? '100%' : '104%'}
                  fill={contrastChart[0]}
                  stroke={0}
                  isAnimationActive={false}
                />
                <Legend
                  content={
                    <CustomLegend
                      thresholdsData={thresholds}
                      isAreaThresholds={isAreaThresholds}
                      value={value}
                    />
                  }
                  height={36}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default GaugeVisualization;
